/**
 * lib/passwordConfig.ts
 *
 * Reads PASSWORD_COMPLEXITY and SHOW_CONFIRM_PASSWORD env vars.
 * Safe to import in both server and client code - values are embedded at
 * build time via NEXT_PUBLIC_ prefix (so they're readable client-side).
 *
 * Levels:
 *   1 = very strong: 2 uppercase + 1 special + 8 chars
 *   2 = normal      : 1 uppercase + 1 special + 6 chars  (default)
 *   3 = loose       : 6 chars minimum, any chars
 *   4 = very loose  : 4 chars minimum
 */

export type PasswordLevel = 1 | 2 | 3 | 4;

const raw = process.env.NEXT_PUBLIC_PASSWORD_COMPLEXITY ?? '2';
export const PASSWORD_LEVEL: PasswordLevel = ([1, 2, 3, 4].includes(Number(raw))
  ? Number(raw)
  : 2) as PasswordLevel;

export const SHOW_CONFIRM_PASSWORD: boolean =
  (process.env.NEXT_PUBLIC_SHOW_CONFIRM_PASSWORD ?? '1') !== '0';

/** Returns regex + human-readable hint for the current level */
export function getPasswordPolicy(): { regex: RegExp; hint: string; hintPt: string } {
  switch (PASSWORD_LEVEL) {
    case 1:
      return {
        regex: /^(?=.*[A-Z].*[A-Z])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
        hint: 'Min 8 chars, 2 uppercase, 1 special character',
        hintPt: 'Mín. 8 caracteres, 2 maiúsculas, 1 especial',
      };
    case 2:
      return {
        regex: /^(?=.*[A-Z]).{6,}$/,
        hint: 'Min 6 chars, 1 uppercase',
        hintPt: 'Mín. 6 caracteres, 1 maiúscula',
      };
    case 3:
      return {
        regex: /^.{6,}$/,
        hint: 'Min 6 characters',
        hintPt: 'Mín. 6 caracteres',
      };
    case 4:
      return {
        regex: /^.{4,}$/,
        hint: 'Min 4 characters',
        hintPt: 'Mín. 4 caracteres',
      };
  }
}
